/**
 * Clear lake water with a stony bed you can see into.
 *
 * The lake is still a single flat, opaque sheet — there is no modelled lake
 * bed and no extra render pass. The shader fakes what the eye expects from
 * shallow, clear Nordic water:
 *
 *   • moving ripples: a handful of directional waves on the deep-water
 *     dispersion relation (ω = √(g·k)) perturb the surface normal, so the sun
 *     and the sky break into glints and the swell drifts at a believable
 *     speed. Waves narrower than a pixel fade out instead of aliasing.
 *   • a pebble bed: the view ray is refracted through the rippled surface and
 *     walked down to a virtual bed whose depth grows with the distance from
 *     the waterline (`aShore`). The bed is two Voronoi layers — gravel and
 *     sparse larger stones with algae — so it wobbles under the ripples.
 *   • caustics: the familiar dancing light net, only on the shallow bed.
 *   • depth: Beer–Lambert absorption (red first) plus in-scatter turns the
 *     golden shallows teal and then dark further out.
 *   • a thin, lapping wet line where the water meets the sand.
 *
 * The result goes into `diffuseColor`, so the bed is lit by the scene's own
 * sun, sky and shadows (the sauna shades the shallows) and dims at dusk
 * without a separate day/night code path.
 *
 * Motion reads `uLakeTime`, which the caller advances from
 * `resolveAmbientEffectTime` (pinned under automation).
 */
import * as THREE from "three";

export const LAKE_WATER_PATCH_KEY = "lake-water-v1";

/**
 * Put ahead of `LAKE_BED_GLSL` for the low-tier lake: four ripple waves
 * instead of nine, only the mid-size pebbles, no caustics and banded foam
 * instead of a cellular one. Same look at a glance, a fraction of the ALU.
 */
export const LAKE_LITE_DEFINE = "#define LAKE_LITE";

export interface LakeWaterUniforms {
	uLakeTime: { value: number };
	/** Caustic strength on the bed (0 = off). Lower after sundown. */
	uLakeCaustics: { value: number };
	/** Colour the water column scatters back (linear RGB). */
	uLakeScatter: { value: THREE.Color };
	/** Per-metre absorption, red → blue (linear). */
	uLakeAbsorb: { value: THREE.Vector3 };
	/** Ripple slope multiplier (0 = glassy). */
	uLakeRipple: { value: number };
}

export function createLakeWaterUniforms(): LakeWaterUniforms {
	return {
		uLakeTime: { value: 0 },
		uLakeCaustics: { value: 1 },
		uLakeScatter: { value: new THREE.Color(0.03, 0.075, 0.075) },
		uLakeAbsorb: { value: new THREE.Vector3(0.5, 0.15, 0.13) },
		uLakeRipple: { value: 0.85 },
	};
}

/**
 * Shared GLSL: hashing, Voronoi pebbles, caustics, ripple slope and bed
 * depth. Also used by the sand band so the pebbles run out of the water.
 */
export const LAKE_BED_GLSL = /* glsl */ `
float lakeHash1(float n) { return fract(sin(n) * 43758.5453123); }
vec2 lakeHash2(vec2 p) {
	p = vec2(dot(p, vec2(127.1, 311.7)), dot(p, vec2(269.5, 183.3)));
	return fract(sin(p) * 43758.5453);
}

// x: distance to the nearest cell centre, y: distance to the cell edge
// (F2 - F1), z: a stable per-cell random, w: a second per-cell random.
vec4 lakeVoronoi(vec2 p, out vec2 toCentre) {
	vec2 cell = floor(p);
	vec2 local = fract(p);
	float f1 = 8.0;
	float f2 = 8.0;
	vec2 best = vec2(0.0);
	toCentre = vec2(0.0);
	for (int j = -1; j <= 1; j++) {
		for (int i = -1; i <= 1; i++) {
			vec2 o = vec2(float(i), float(j));
			vec2 r = o + 0.1 + 0.8 * lakeHash2(cell + o) - local;
			float d = dot(r, r);
			if (d < f1) {
				f2 = f1;
				f1 = d;
				best = cell + o;
				toCentre = r;
			} else if (d < f2) {
				f2 = d;
			}
		}
	}
	f1 = sqrt(f1);
	f2 = sqrt(f2);
	vec2 h = lakeHash2(best + 17.3);
	return vec4(f1, f2 - f1, h.x, h.y);
}

vec3 lakeStoneColour(float h) {
	vec3 golden = vec3(0.40, 0.32, 0.20);
	vec3 grey = vec3(0.31, 0.30, 0.28);
	vec3 rust = vec3(0.40, 0.25, 0.15);
	vec3 dark = vec3(0.15, 0.145, 0.13);
	vec3 pale = vec3(0.47, 0.43, 0.36);
	if (h < 0.3) return mix(golden, pale, h / 0.3);
	if (h < 0.55) return mix(grey, golden, (h - 0.3) / 0.25);
	if (h < 0.75) return mix(rust, grey, (h - 0.55) / 0.2);
	return mix(dark, grey, (h - 0.75) / 0.25);
}

// One layer of rounded stones lying on the bed: a disc of random radius in
// most Voronoi cells, domed shading, a soft contact shadow round the rim.
// Returns premultiplied colour in rgb and coverage in a.
vec4 lakeStoneLayer(vec2 p, float fill, float salt) {
	// A gentle domain warp keeps the stones from reading as circles.
	vec2 q = p + salt + 0.22 * vec2(sin(p.y * 1.3 + salt), sin(p.x * 1.1 - salt));
	vec2 toCentre;
	vec4 v = lakeVoronoi(q, toCentre);
	float radius = 0.26 + 0.22 * v.w;
	float present = step(1.0 - fill, fract(v.z * 5.3 + salt));
	float rim = smoothstep(radius, radius - 0.07, v.x);
	float t = clamp(v.x / radius, 0.0, 1.0);
	float dome = 0.45 + 0.55 * sqrt(1.0 - t * t);
	// Lit from the key light's side, shaded on the far flank.
	float facing = dot(-toCentre / radius, normalize(vec2(0.55, -0.65)));
	vec3 colour = lakeStoneColour(v.z) * dome * (0.85 + 0.3 * v.w) * (0.88 + 0.28 * facing);
	float shadow = smoothstep(radius + 0.12, radius, v.x) * (1.0 - rim);
	return vec4(colour * rim, rim) * present + vec4(0.0, 0.0, 0.0, shadow * 0.45 * present);
}

vec3 lakeOverStones(vec3 base, vec4 layer) {
	// a < coverage means part of the alpha is rim shadow darkening the base.
	return base * (1.0 - layer.a) + layer.rgb;
}

// Sand and gravel with scattered rounded stones in three sizes. fp: world
// metres per pixel; detail the pixel cannot resolve fades to its average.
vec3 lakePebbles(vec2 p, float fp, float fill) {
	float grain = lakeHash2(floor(p * 90.0)).x;
	vec3 sand = vec3(0.34, 0.29, 0.20) * (0.88 + 0.24 * grain * (1.0 - smoothstep(0.004, 0.012, fp)));
	vec3 bed = sand;
#ifndef LAKE_LITE
	vec4 small = lakeStoneLayer(p * 15.0, 0.75 * fill, 0.0);
	bed = mix(bed, lakeOverStones(bed, small), 1.0 - smoothstep(0.015, 0.045, fp));
#endif
	vec4 medium = lakeStoneLayer(p * 6.0, 0.55 * fill, 31.7);
	bed = mix(bed, lakeOverStones(bed, medium), 1.0 - smoothstep(0.04, 0.11, fp));
#ifndef LAKE_LITE
	vec4 large = lakeStoneLayer(p * 2.1, 0.32 * fill, 71.3);
	large.rgb = mix(large.rgb, vec3(0.16, 0.2, 0.09) * large.a, 0.35 * smoothstep(0.4, 0.95, fract(large.a * 13.1 + p.x * 0.3)));
	bed = mix(bed, lakeOverStones(bed, large), 1.0 - smoothstep(0.12, 0.32, fp));
#endif
	return bed;
}

// Tileable caustic net (period 2π / 2.2 m).
float lakeCaustic(vec2 p, float t) {
	vec2 q = mod(p * 2.2, 6.2831853) - 250.0;
	vec2 i = q;
	float c = 1.0;
	float inten = 0.005;
	for (int n = 0; n < 4; n++) {
		float tt = t * 0.55 * (1.0 - (3.5 / float(n + 1)));
		i = q + vec2(cos(tt - i.x) + sin(tt + i.y), sin(tt - i.y) + cos(tt + i.x));
		c += 1.0 / length(vec2(q.x / (sin(i.x + tt) / inten), q.y / (cos(i.y + tt) / inten)));
	}
	c /= 4.0;
	c = 1.17 - pow(c, 1.4);
	return clamp(pow(abs(c), 8.0), 0.0, 2.5);
}

#ifdef LAKE_LITE
#define LAKE_WAVES 4
#else
#define LAKE_WAVES 9
#endif
// Surface slope (dh/dx, dh/dz) from directional waves on the deep-water
// dispersion relation. Waves shorter than ~4 pixels fade out.
vec2 lakeRippleSlope(vec2 p, float t, float fp) {
	vec2 g = vec2(0.0);
	for (int i = 0; i < LAKE_WAVES; i++) {
		float fi = float(i);
		// The lake lies beyond the +Z shore; crests travel inland along -Z.
		float ang = -1.5707963 + (lakeHash1(fi * 7.13 + 1.0) - 0.5) * 0.7;
		vec2 d = vec2(cos(ang), sin(ang));
		float lambda = 3.2 * pow(0.66, fi);
		float k = 6.2831853 / lambda;
		float w = sqrt(9.81 * k);
		float slope = 0.085 * (0.7 + 0.6 * lakeHash1(fi * 3.7 + 2.0));
		float resolve = 1.0 - smoothstep(lambda * 0.12, lambda * 0.35, fp);
		g += d * slope * cos(k * dot(d, p) - w * t + fi * 1.7) * resolve;
	}
	return g;
}

// Bed depth below the surface (m) from the distance to the waterline (m).
float lakeBedDepth(float shore) {
	return 0.02 + 2.3 * (1.0 - exp(-shore * 0.17));
}
`;

const VERTEX_PARS = /* glsl */ `
attribute float aShore;
varying float vShore;
varying vec3 vLakeWorld;`;

const VERTEX_BODY = /* glsl */ `
vShore = aShore;
vLakeWorld = (modelMatrix * vec4(transformed, 1.0)).xyz;`;

const FRAGMENT_PARS = /* glsl */ `
uniform float uLakeTime;
uniform float uLakeCaustics;
uniform vec3 uLakeScatter;
uniform vec3 uLakeAbsorb;
uniform float uLakeRipple;
varying float vShore;
varying vec3 vLakeWorld;
${LAKE_BED_GLSL}`;

/** Runs right after `color_fragment`: builds the rippled normal + the bed. */
const FRAGMENT_COLOUR = /* glsl */ `
vec3 lakeView = normalize(vLakeWorld - cameraPosition);
vec2 lakeFw = fwidth(vLakeWorld.xz);
float lakeFp = max(max(lakeFw.x, lakeFw.y), 1e-4);
vec2 lakeSlope = lakeRippleSlope(vLakeWorld.xz, uLakeTime, lakeFp) * uLakeRipple;
vec3 lakeN = normalize(vec3(-lakeSlope.x, 1.0, -lakeSlope.y));
float lakeDepth = lakeBedDepth(vShore);
vec3 lakeRay = refract(lakeView, lakeN, 1.0 / 1.333);
float lakeRun = lakeDepth / max(0.12, -lakeRay.y);
vec2 lakeBedXZ = vLakeWorld.xz + lakeRay.xz * lakeRun;
vec3 lakeBed = lakePebbles(lakeBedXZ, lakeFp * (1.0 + lakeRun * 0.35), 1.0);
#ifndef LAKE_LITE
float lakeCausticFade = exp(-lakeDepth * 0.9) * (1.0 - smoothstep(0.05, 0.2, lakeFp));
lakeBed *= 1.0 + lakeCaustic(lakeBedXZ, uLakeTime) * uLakeCaustics * 1.6 * lakeCausticFade;
#endif
float lakePath = lakeRun + lakeDepth;
vec3 lakeColour = lakeBed * exp(-uLakeAbsorb * lakePath)
	+ uLakeScatter * (1.0 - exp(-1.2 * lakePath));
float lakeLap = 0.12 + 0.05 * sin(uLakeTime * 1.1) * (0.7 + 0.3 * sin(vLakeWorld.x * 1.7))
	+ 0.03 * sin(uLakeTime * 0.7) * sin(vLakeWorld.x * 3.1);
#ifdef LAKE_LITE
float lakeLace = 0.5 + 0.5 * sin(vLakeWorld.x * 23.0 + sin(vLakeWorld.z * 17.0 + uLakeTime * 0.6) * 2.0);
#else
vec2 lakeFoamCell;
float lakeLace = lakeVoronoi(vLakeWorld.xz * 7.0 + vec2(0.0, uLakeTime * 0.08), lakeFoamCell).y;
#endif
float lakeFoam = (1.0 - smoothstep(lakeLap * 0.25, lakeLap, vShore))
	* smoothstep(0.12, 0.32, lakeLace);
// The last centimetres are wet and dark, with a broken lace of foam.
lakeColour *= 0.75 + 0.25 * smoothstep(0.0, 0.25, vShore);
lakeColour = mix(lakeColour, vec3(0.5, 0.52, 0.5), clamp(lakeFoam, 0.0, 1.0) * 0.3);
diffuseColor.rgb = lakeColour;`;

/** Runs right after `roughnessmap_fragment`: calm far water stays glossy. */
const FRAGMENT_ROUGHNESS = /* glsl */ `
roughnessFactor = max(roughnessFactor, clamp(lakeFoam, 0.0, 1.0) * 0.5);`;

/** Runs right after `normal_fragment_maps`: view-space rippled normal. */
const FRAGMENT_NORMAL = /* glsl */ `
normal = normalize((viewMatrix * vec4(lakeN, 0.0)).xyz);`;

function replaceChunk(
	source: string,
	chunk: string,
	replacement: string,
): string {
	if (!source.includes(chunk)) {
		throw new Error(`[lakeWater] Missing shader chunk ${chunk}`);
	}
	return source.replace(chunk, replacement);
}

function patchLakeWaterShader(
	shader: THREE.WebGLProgramParametersWithUniforms,
	uniforms: LakeWaterUniforms,
	lite: boolean,
): void {
	Object.assign(shader.uniforms, uniforms);
	shader.vertexShader = replaceChunk(
		shader.vertexShader,
		"#include <common>",
		`#include <common>${VERTEX_PARS}`,
	);
	shader.vertexShader = replaceChunk(
		shader.vertexShader,
		"#include <begin_vertex>",
		`#include <begin_vertex>${VERTEX_BODY}`,
	);
	shader.fragmentShader = replaceChunk(
		shader.fragmentShader,
		"#include <common>",
		`#include <common>${lite ? `\n${LAKE_LITE_DEFINE}` : ""}${FRAGMENT_PARS}`,
	);
	shader.fragmentShader = replaceChunk(
		shader.fragmentShader,
		"#include <color_fragment>",
		`#include <color_fragment>${FRAGMENT_COLOUR}`,
	);
	shader.fragmentShader = replaceChunk(
		shader.fragmentShader,
		"#include <roughnessmap_fragment>",
		`#include <roughnessmap_fragment>${FRAGMENT_ROUGHNESS}`,
	);
	shader.fragmentShader = replaceChunk(
		shader.fragmentShader,
		"#include <normal_fragment_maps>",
		`#include <normal_fragment_maps>${FRAGMENT_NORMAL}`,
	);
}

/**
 * drei's `MeshReflectorMaterial` multiplies the mirror image into the
 * diffuse colour, which would tint the pebbles with the sauna. Reroute it:
 * the ripples distort the mirror lookup and the mirror is ADDED with a
 * Fresnel weight, like a real water surface. If a drei upgrade moves these
 * lines the reflector silently keeps its own mixing — the lake still works.
 */
const REFLECTOR_LOOKUP = "vec4 new_vUv = my_vUv;";
const REFLECTOR_MIX =
	"diffuseColor.rgb = diffuseColor.rgb * ((1.0 - min(1.0, mirror)) + newMerge.rgb * mixStrength);";

function rerouteReflector(shader: THREE.WebGLProgramParametersWithUniforms) {
	if (
		!shader.fragmentShader.includes(REFLECTOR_LOOKUP) ||
		!shader.fragmentShader.includes(REFLECTOR_MIX)
	) {
		return;
	}
	shader.fragmentShader = shader.fragmentShader
		.replace(
			REFLECTOR_LOOKUP,
			`${REFLECTOR_LOOKUP}
      new_vUv.xy += lakeSlope * 0.06 * new_vUv.w;`,
		)
		.replace(
			REFLECTOR_MIX,
			`float lakeFresnel = 0.02 + 0.98 * pow(1.0 - max(dot(-lakeView, lakeN), 0.0), 5.0);
      totalEmissiveRadiance += newMerge.rgb * mixStrength * mirror * lakeFresnel;`,
		);
}

/**
 * Patch a standard (or drei reflector) material into lake water. Chains any
 * hook already on the material and extends its program cache key.
 */
export function applyLakeWater<T extends THREE.MeshStandardMaterial>(
	material: T,
	uniforms: LakeWaterUniforms,
	{ lite = false }: { lite?: boolean } = {},
): T {
	const previous = material.onBeforeCompile;
	const previousKey = material.customProgramCacheKey;
	material.onBeforeCompile = (shader, renderer) => {
		previous.call(material, shader, renderer);
		patchLakeWaterShader(shader, uniforms, lite);
		rerouteReflector(shader);
	};
	material.customProgramCacheKey = () =>
		`${previousKey.call(material)}|${LAKE_WATER_PATCH_KEY}${lite ? "|lite" : ""}`;
	material.userData.lakeUniforms = uniforms;
	material.needsUpdate = true;
	return material;
}

/** The lake uniforms a patched material draws with, if any. */
export function lakeUniformsOf(
	material: THREE.Material | THREE.Material[] | undefined,
): LakeWaterUniforms | undefined {
	if (!material || Array.isArray(material)) return undefined;
	return material.userData.lakeUniforms as LakeWaterUniforms | undefined;
}
